import { createSlice } from "@reduxjs/toolkit";
import { Conversation } from "./chatSlice";
import { pingFrame } from "../actions/sharedActions";
import { PingMessage, PingResponse } from "../types/api";
import { AnyMessage } from "../chat/types";

export interface ConversationsState {
  data: Conversation[];
  status: "idle" | "loading" | "succeeded" | "failed";
  error: string | null;
}

const initialState: ConversationsState = {
  data: [],
  status: "idle",
  error: null,
};

const transformApiToLocal = (
  conversations: PingResponse["data"]["conversations"]
) =>
  (conversations || []).map((c) => ({
    ...c,
    messages: c.messages.map(transformApiToLocalMessage),
  }));

function transformApiToLocalMessage(m: PingMessage): AnyMessage {
  return m.role === "assistant"
    ? {
        ...m,
        metadata: {
          ...m.metadata,
          handoff:
            m.metadata.handoff != null
              ? {
                  ...m.metadata.handoff,
                  user_email: "",
                }
              : null,
        },
        role: "assistant",
        rating: m.metadata.rating,
        isInitial: m.role == "assistant" && m.sender != "assistant",
        isGenerating: false,
      }
    : {
        ...m,
        role: "user",
        isInitial: false,
      };
}

// NOTE(liamvdv): Merge Conversations; Longer Conversation Win. If equally long, remote conversation wins.
//                This ensures updates in e. g. handoff, rating.
const reconcileConversations = (
  local: Conversation[],
  remote: Conversation[]
) => {
  return [...local, ...remote].reduce((acc, cur) => {
    const existingIndex = acc.findIndex(
      (c) => c.conversation_id === cur.conversation_id
    );
    if (existingIndex !== -1) {
      // longer conversations win.
      if (acc[existingIndex].messages.length <= cur.messages.length)
        acc[existingIndex] = cur;
    } else {
      acc.push(cur);
    }
    return acc;
  }, [] as Conversation[]);
};

const conversationOrdering = (conversations: Conversation[]) =>
  conversations.sort(
    (a, b) =>
      new Date(b.created_at).getTime() - new Date(a.created_at).getTime() // order is descending (latest first)
  );

const conversationsSlice = createSlice({
  name: "conversations",
  initialState,
  reducers: {
    upsertConversation(state, action: { payload: Conversation }) {
      const conversation = action.payload;
      const existingIndex = state.data.findIndex(
        (c) => c.conversation_id === conversation.conversation_id
      );
      if (existingIndex !== -1) {
        state.data[existingIndex] = conversation;
      } else {
        state.data = [conversation, ...state.data];
      }
      state.data = conversationOrdering(state.data);
    },
  },
  extraReducers: (builder) =>
    builder
      .addCase(pingFrame.fulfilled, (state, action) => {
        const apiData = transformApiToLocal(action.payload.data.conversations);
        const isMerge = action.meta.arg.background;
        const updated = isMerge
          ? reconcileConversations(state.data, apiData)
          : apiData;
        state.data = conversationOrdering(updated);
        state.status = "succeeded";
      })
      .addCase(pingFrame.rejected, (state, action) => {
        if (action.meta.arg.background) return;
        state.data = [];
        state.status = "failed";
        state.error = action.error.message || null;
      }),
});

export const { upsertConversation } = conversationsSlice.actions;
export const conversationsReducer = conversationsSlice.reducer;
